import { AddCircle } from '@mui/icons-material';
import { Box, Button, Grid, Paper } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import SearchField from 'src/shared/components/form/searchField';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import PageTabNavigation, { PageTabItem } from 'src/shared/components/navigations/pageTabNavigation';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { getAllInvoiceArticleCategory } from 'src/shared/services/invoiceArticleCategoryService';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { CreateInvoiceArticle } from '../components/createInvoiceArticle';
import { InvoiceArticleDataGrid } from '../components/invoiceArticleDataGrid';
import { ArticleRegisterSearchFilter } from '../models/articleRegisterSearchFilter';
import { InvoiceArticleCategory } from '../models/invoiceArticleCategory';
import { InvoiceArticleCategorySearchFilter } from '../models/invoiceArticleCategorySearchFilter';
import { EditInvoiceArticle } from '../components/editInvoiceArticle';

export const ArticleRegisterPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [openCreateArticle, setOpenCreateArticle] = useState<boolean>(false);
  const [openEditArticle, setOpenEditArticle] = useState<boolean>(false);
  const [articleId, setArticleId] = useState<number | undefined>(undefined);
  const [, setLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<InvoiceArticleCategory[]>();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [refetchDataGrid, setRefetchDataGrid] = useState<boolean>(false);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);

  const [articleRegisterFilter, setArticleRegisterFilter] = useLocalStorage(LocalStorageKeys.ArticleRegisterFilter, {
    sortCol: 'name',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as ArticleRegisterSearchFilter);

  const categoryFilter = useMemo<InvoiceArticleCategorySearchFilter>(
    () => ({
      pageIndex: 0,
      pageSize: 1000,
      sortCol: 'articleCategory',
      sortDir: 'asc',
      filter: '',
    }),
    [],
  );

  const searchFilter: ArticleRegisterSearchFilter = useMemo(() => {
    return {
      filter: articleRegisterFilter?.filter,
      pageSize: articleRegisterFilter?.pageSize,
      pageIndex: articleRegisterFilter?.pageIndex,
      sortCol: articleRegisterFilter?.sortCol,
      sortDir: articleRegisterFilter?.sortDir,
      articleCategory_id: selectedCategoryId,
    } as ArticleRegisterSearchFilter;
  }, [
    articleRegisterFilter?.filter,
    articleRegisterFilter?.pageIndex,
    articleRegisterFilter?.pageSize,
    articleRegisterFilter?.sortCol,
    articleRegisterFilter?.sortDir,
    selectedCategoryId,
  ]);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllInvoiceArticleCategory(categoryFilter);
      if (isMounted()) {
        const allCategories = result.data.results;

        // Skapar en kategori (Övrigt)
        const otherCategory = {
          articleCategory_id: 0,
          articleCategory: t('invoice:articleRegister.other'),
          product_id: 0,
          articles: [],
        };

        const mergedCategories = [otherCategory, ...allCategories];
        setCategories(mergedCategories);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [categoryFilter, isMounted, t]);

  useEffect(() => {
    if (isMounted()) {
      fetchCategories();
    }
  }, [fetchCategories, isMounted]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setArticleRegisterFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setArticleRegisterFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setArticleRegisterFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setArticleRegisterFilter, tempSearchString]);

  const pageTabs: PageTabItem[] = [
    {
      label: t('invoice:articleRegisterTabs.articles'),
      route: RoutesEnum.InvoiceArticleRegister,
    },
    {
      label: t('invoice:articleRegisterTabs.category'),
      route: RoutesEnum.InvoiceArticleCategory,
    },
  ];

  return (
    <GridWrapper>
      <Grid
        item
        xs={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <MainContentHeader title={t('invoice:articleRegister.header')} />
      </Grid>

      <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={() => setOpenCreateArticle(true)}
          sx={{ marginRight: 2 }}
          variant='contained'
          color='primary'
          startIcon={<AddCircle fontSize='small' />}
          disableElevation
        >
          {t('invoice:articleRegister.createArticle')}
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <PageTabNavigation pageTabs={pageTabs} />
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue={searchFilter.filter || ''}
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
            rightContent={
              categories &&
              categories.length > 0 && (
                <Box sx={{ display: 'flex' }}>
                  <AutocompleteDropdown<InvoiceArticleCategory>
                    keyProp='articleCategory_id'
                    nameProp='articleCategory'
                    label={t('invoice:articleRegister.category')}
                    emptyLabel={categories?.length === 0 ? t('invoice:articleRegister.noCategories') : t('common:button.choose')}
                    placeholder={t('common:button.choose')}
                    options={categories || []}
                    callback={(selectedOption) => {
                      if (selectedOption) {
                        setSelectedCategoryId(selectedOption.articleCategory_id);
                        setArticleRegisterFilter((prev) => ({
                          ...prev,
                          pageIndex: 0,
                        }));
                      } else {
                        setSelectedCategoryId(undefined);
                        setArticleRegisterFilter((prev) => ({
                          ...prev,
                          pageIndex: 0,
                        }));
                      }
                    }}
                  />
                </Box>
              )
            }
          />
          <Box className='section-inset'>
            <InvoiceArticleDataGrid
              refetchDataGrid={refetchDataGrid}
              setRefetchDataGrid={setRefetchDataGrid}
              setArticleId={setArticleId}
              setOpenEditArticle={setOpenEditArticle}
              searchFilter={searchFilter}
              searchFilterCallback={(newFilter) =>
                setArticleRegisterFilter((prev) => ({
                  ...prev,
                  ...newFilter,
                }))
              }
            />
          </Box>
        </Paper>
      </Grid>
      {openCreateArticle && <CreateInvoiceArticle setOpenCreateArticle={setOpenCreateArticle} setRefetchDataGrid={setRefetchDataGrid} />}
      {openEditArticle && articleId && (
        <EditInvoiceArticle
          key={articleId}
          articleId={articleId}
          setArticleId={setArticleId}
          setOpenEditArticle={setOpenEditArticle}
          setRefetchDataGrid={setRefetchDataGrid}
        />
      )}
    </GridWrapper>
  );
};
